import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Paper, Typography, Stack, Chip, Button, IconButton,
  TextField, InputAdornment, CircularProgress, Tooltip,
  ToggleButton, ToggleButtonGroup, Divider, Avatar,
} from '@mui/material';
import {
  Close, Search, NoteAlt, Person, AccessTime, TodayOutlined,
} from '@mui/icons-material';
import axios from 'axios';
import moment from 'moment';
import { loadNurseNotesdb } from '../BaseURL';

// ── Theme tokens ──────────────────────────────────────────────────────
const PRIMARY      = '#1A9B7F';
const PRIMARY_BG   = 'rgba(26,155,127,0.10)';
const BORDER       = '#e5e9ef';
const MUTED        = '#64748b';
const STRONG       = '#0f172a';

const LABEL_SX = {
  fontSize: '0.66rem', fontWeight: 700, color: '#94a3b8',
  textTransform: 'uppercase', letterSpacing: '0.6px',
};

/**
 * Read-only dialog that pulls all nurse notes for the selected patient and
 * lays them out chronologically, grouped by date. Doctors use this from the
 * InPatient panel to review what nursing staff have observed.
 *
 * Props:
 *   open, onClose
 *   patient   { id, fullName, age, gender, visitDate }
 */
export default function NurseNotesDialog({ open, onClose, patient }) {
  const [loading, setLoading] = useState(false);
  const [notes, setNotes]     = useState([]);
  const [search, setSearch]   = useState('');
  const [dateFilter, setDateFilter] = useState('all'); // all | today | 7d
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!open || !patient?.id) return;
    setLoading(true); setError(null);
    axios.get(loadNurseNotesdb, {
      params: { patientId: patient.id, visitDate: patient.visitDate },
    })
      .then(res => {
        if (res.data?.success) setNotes(res.data.notes || []);
        else { setError(res.data?.message || 'Failed to load notes'); setNotes([]); }
      })
      .catch(err => {
        console.error('loadNurseNotes failed', err);
        setError('Could not load nurse notes.');
        setNotes([]);
      })
      .finally(() => setLoading(false));
  }, [open, patient?.id, patient?.visitDate]);

  // Filtered, sorted, grouped by date
  const groups = useMemo(() => {
    const today = moment().startOf('day');
    const filtered = (notes || []).filter(n => {
      if (search.trim()) {
        const q = search.toLowerCase();
        if (
          !((n.note || '').toLowerCase().includes(q) ||
            (n.user || '').toLowerCase().includes(q))
        ) return false;
      }
      if (dateFilter === 'today') return moment(n.dat).isSame(today, 'day');
      if (dateFilter === '7d') return moment(n.dat).isAfter(today.clone().subtract(7, 'days'));
      return true;
    });

    // Sort newest first
    filtered.sort((a, b) => {
      const ka = moment(`${a.dat} ${a.time || '00:00'}`, 'YYYY-MM-DD HH:mm:ss');
      const kb = moment(`${b.dat} ${b.time || '00:00'}`, 'YYYY-MM-DD HH:mm:ss');
      return kb.valueOf() - ka.valueOf();
    });

    const map = new Map();
    filtered.forEach(n => {
      const key = n.dat || 'Unknown';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(n);
    });
    return [...map.entries()].sort((a, b) => moment(b[0]).diff(moment(a[0])));
  }, [notes, search, dateFilter]);

  const totals = useMemo(() => {
    const today = moment().startOf('day');
    const todays = (notes || []).filter(n => moment(n.dat).isSame(today, 'day')).length;
    const nurses = new Set((notes || []).map(n => n.user)).size;
    return { all: notes?.length || 0, todays, nurses };
  }, [notes]);

  const initialsOf = (name = '') =>
    name.split(/\s+/).filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase() || '?';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 3, height: '85vh' } }}
    >
      {/* Header */}
      <DialogTitle sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, py: 2,
        bgcolor: PRIMARY, color: 'white',
      }}>
        <NoteAlt />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2 }}>
            Nurse Notes
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', opacity: 0.9 }}>
            {patient?.fullName ? `${patient.fullName} · ID ${patient.id}` : 'Select a patient'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2, bgcolor: '#f8fafc' }}>
        {/* Summary strip */}
        <Box sx={{
          mb: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.75,
          alignItems: 'center',
        }}>
          <SummaryPill label="Total"  value={totals.all}    color={STRONG} tint="#f1f5f9" />
          <SummaryPill label="Today"  value={totals.todays} color="#15803D" tint="rgba(22,163,74,0.10)" />
          <SummaryPill label="Nurses" value={totals.nurses} color={PRIMARY}  tint={PRIMARY_BG} />
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ fontSize: '0.74rem', color: MUTED }}>
            {(notes || []).length} note{(notes || []).length === 1 ? '' : 's'} on file
          </Typography>
        </Box>

        {/* Filter row */}
        <Paper elevation={0} sx={{
          p: 1, mb: 1.5, borderRadius: 2,
          border: `1px solid ${BORDER}`, bgcolor: '#fff',
          display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center',
        }}>
          <TextField
            size="small" placeholder="Search notes or nurse name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 220 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" sx={{ color: '#94a3b8' }} />
                </InputAdornment>
              ),
            }}
          />
          <ToggleButtonGroup
            size="small" exclusive
            value={dateFilter}
            onChange={(_, v) => v && setDateFilter(v)}
          >
            {[
              { v: 'all',   l: 'All' },
              { v: 'today', l: 'Today' },
              { v: '7d',    l: 'Last 7 days' },
            ].map(o => (
              <ToggleButton key={o.v} value={o.v} sx={{
                textTransform: 'none', fontWeight: 700, fontSize: '0.78rem',
                '&.Mui-selected': { bgcolor: PRIMARY_BG, color: PRIMARY },
              }}>{o.l}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Paper>

        {/* Body */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} sx={{ color: PRIMARY }} />
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography sx={{ color: '#991B1B', fontWeight: 700 }}>{error}</Typography>
          </Box>
        ) : groups.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <NoteAlt sx={{ fontSize: 56, color: '#cbd5e1', mb: 1 }} />
            <Typography sx={{ color: MUTED, fontSize: '0.92rem', fontWeight: 700 }}>
              {(notes || []).length === 0
                ? 'No nurse notes on file for this patient'
                : 'No notes match the current filter'}
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {groups.map(([date, items]) => (
              <Box key={date}>
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5, pl: 0.25 }}>
                  <TodayOutlined sx={{ fontSize: 14, color: MUTED }} />
                  <Typography sx={LABEL_SX}>
                    {moment(date).isValid() ? moment(date).format('dddd, D MMM YYYY') : date}
                  </Typography>
                  <Chip size="small" label={`${items.length} note${items.length === 1 ? '' : 's'}`}
                    sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#f1f5f9', color: MUTED }} />
                </Stack>

                <Stack spacing={0.75}>
                  {items.map((n, i) => (
                    <Paper key={n.trans_code || `${n.dat}-${n.time}-${i}`} elevation={0}
                      sx={{
                        p: 1.5, borderRadius: 2,
                        border: `1px solid ${BORDER}`, bgcolor: '#fff',
                      }}>
                      <Stack direction="row" alignItems="flex-start" spacing={1.25}>
                        <Avatar sx={{
                          width: 32, height: 32, fontSize: 12, fontWeight: 700,
                          bgcolor: PRIMARY_BG, color: PRIMARY,
                        }}>
                          {initialsOf(n.user)}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: STRONG }}>
                              {n.user || 'Unknown nurse'}
                            </Typography>
                            <Tooltip title="Recorded time" arrow>
                              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: MUTED }}>
                                <AccessTime sx={{ fontSize: 13 }} />
                                <Typography sx={{ fontSize: '0.74rem' }}>
                                  {n.time
                                    ? moment(n.time, ['HH:mm:ss','HH:mm','hh:mm A']).format('hh:mm A')
                                    : '—'}
                                </Typography>
                              </Stack>
                            </Tooltip>
                          </Stack>
                          <Typography sx={{
                            fontSize: '0.88rem', color: '#0f172a',
                            whiteSpace: 'pre-wrap', lineHeight: 1.45,
                          }}>
                            {n.note || '—'}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.25, bgcolor: '#fff', borderTop: `1px solid ${BORDER}` }}>
        <Button onClick={onClose} variant="contained"
          sx={{
            bgcolor: PRIMARY, '&:hover': { bgcolor: '#157763' },
            textTransform: 'none', fontWeight: 700,
          }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Local helper ──────────────────────────────────────────────────────
function SummaryPill({ label, value, color, tint }) {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 0.5,
      px: 1, py: 0.4, borderRadius: 1.25,
      bgcolor: tint, color,
    }}>
      <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '0.9rem', fontWeight: 800, lineHeight: 1 }}>
        {value}
      </Typography>
    </Box>
  );
}
